import React, { ReactNode } from 'react';
import Grid from '@material-ui/core/Grid';
import { customTheme as theme } from '../styles';
import T from 'i18n-react';
import {
  LocationSubheader,
  MIN_IMAGE_NUMBER,
  PlanSlick,
  ContentSummary,
} from './index';
import Typography from '@material-ui/core/Typography';
import baseStyles from './styles';
import NoImage from '../../../images/no-image.png';
import { BusinessAttractions } from '../Businesses';
import Skeleton from '@material-ui/lab/Skeleton';
import clsx from 'clsx';
import People from '../People';
import PlanContext from './context';
import PlanTermContainer from './PlanTerm/PlanTermContainer';
import AtmosphereSliders from './components/AtmosphereSliders';
import CategoryChip from './components/CategoryChip';

export const styles = {
  root: {
    margin: theme.spacing(2, 0),
  },
  container: {
    maxWidth: theme.breakpoints.values.lg,
    width: '95%',
    margin: theme.spacing(6, 'auto', 2),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(4, 'auto', 2),
    },
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(3, 'auto', 2),
    },
  },
  leftGrid: {
    paddingRight: 0,
    [theme.breakpoints.up('sm')]: {
      paddingRight: theme.spacing(2.5),
    },
    [theme.breakpoints.up('lg')]: {
      paddingRight: theme.spacing(5),
    },
  },
  rightGrid: {
    paddingLeft: 0,
    [theme.breakpoints.up('sm')]: {
      paddingLeft: theme.spacing(2.5),
    },
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(5),
    },
  },
  locationSubheader: {
    marginBottom: theme.spacing(2),
  },
  titleWrapper: {
    margin: theme.spacing(3, 0, 1),
  },
  title: {
    fontSize: 22,
    [theme.breakpoints.up('md')]: {
      fontSize: 32,
    },
  },
  titleSkeleton: {
    width: '100%',
    height: 22 * theme.typography.body1.lineHeight,
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.up('md')]: {
      height: 32 * theme.typography.body1.lineHeight,
    },
  },
  categories: {
    margin: theme.spacing(4, 0),
    display: 'flex',
    flexWrap: 'wrap',
  },
  categorySkeleton: {
    width: 200,
    height: theme.spacing(4),
    borderRadius: theme.shape.borderRadius,
  },
  atmosphereSliderSubtitle: {
    ...baseStyles.subtitle,
    margin: theme.spacing(8, 'auto', 1),
  },
  subtitle: baseStyles.subtitle,
  outerMargin: baseStyles.outerMargin,
};

class Show extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      loaded: false,
    };

    this._mounted = false;
  }

  getPlanId() {
    const {
      match: {
        params: { planId },
      },
    } = this.props;
    return planId;
  }

  _getImages() {
    const { loaded, images = [] } = this.state;
    if (loaded) {
      if (images.length < MIN_IMAGE_NUMBER) {
        return images
          .concat(
            new Array(MIN_IMAGE_NUMBER - images.length).fill({ url: NoImage }),
          )
          .map((i) => i.url || NoImage);
      } else {
        return images.map((i) => i.url);
      }
    }
    return new Array(4).fill('');
  }

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  slickRenderer() {
    return <PlanSlick images={this._getImages()} />;
  }

  titleRenderer() {
    const { classes } = this.props;
    const { loaded, title } = this.state;
    return loaded ? (
      <Typography
        color="textPrimary"
        className={clsx(classes.title, 'text-break-all')}
      >
        {title}
      </Typography>
    ) : (
      <Skeleton variant="rect" className={classes.titleSkeleton} />
    );
  }

  peopleTabRenderer() {
    const { loaded, people = [] } = this.state;
    return <People people={people} loaded={loaded} />;
  }

  termListRenderer() {
    return <PlanTermContainer />;
  }

  _categoriesRenderer(categories) {
    const { classes } = this.props;
    const { loaded } = this.state;

    return loaded ? (
      <div className={clsx(categories.length > 0 && classes.categories)}>
        {categories.map(({ name, kind }) => (
          <CategoryChip label={name} kind={kind} key={name} />
        ))}
      </div>
    ) : (
      <div className={classes.categories}>
        <Skeleton variant="rect" className={classes.categorySkeleton} />
      </div>
    );
  }

  specificCategoriesRenderer() {
    const { specificCategories = [] } = this.state;
    return this._categoriesRenderer(specificCategories);
  }

  skillCategoriesRenderer() {
    const { skillCategories = [] } = this.state;
    return this._categoriesRenderer(skillCategories);
  }

  atmosphereSliderRenderer() {
    const { classes } = this.props;
    const { atmosphereSliders } = this.state;
    return (
      <>
        <Typography className={classes.atmosphereSliderSubtitle}>
          {T.translate('plan.atmosphereSlider')}
        </Typography>
        <AtmosphereSliders atmosphereSliders={atmosphereSliders} />
      </>
    );
  }

  contentSummaryRenderer() {
    const {
      loaded,
      content,
      accommodation,
      amenity,
      amenities,
      isAmenityUnavailable,
      meals,
      mealGrouping,
      nearbyPlace,
      nearbyPlaces,
      isNearbyPlaceUnavailable,
      benefit,
      belonging,
      facility,
      notes,
      comment,
      recruitmentBackground,
    } = this.state;

    return (
      <ContentSummary
        {...{
          loaded,
          content,
          accommodation,
          amenity,
          amenities,
          isAmenityUnavailable,
          meals,
          mealGrouping,
          nearbyPlace,
          nearbyPlaces,
          isNearbyPlaceUnavailable,
          benefit,
          belonging,
          facility,
          notes,
          comment,
          recruitmentBackground,
        }}
      />
    );
  }

  favoriteRenderer() {
    return <div />;
  }

  businessInfoRenderer() {
    return <div />;
  }

  businessAttractionsRenderer() {
    const {
      loaded,
      business: { attractions, address: { region, city } = {} } = {},
    } = this.state;
    return (
      <BusinessAttractions
        loaded={loaded}
        attractions={attractions}
        region={region}
        city={city}
      />
    );
  }

  otherRenderer(): ReactNode {}

  contentRenderer = () => {
    const { classes } = this.props;
    const { loaded, people = [], business: { address = {} } = {} } = this.state;
    const { region, city, cityKana } = address;

    return (
      <>
        <div className={classes.root}>
          {this.slickRenderer()}
          <Grid container className={classes.container}>
            <Grid item xs={12} sm={7} lg={8} className={classes.leftGrid}>
              <div className={classes.locationSubheader}>
                <LocationSubheader {...{ loaded, region, city, cityKana }} />
              </div>
              <div className={classes.titleWrapper}>{this.titleRenderer()}</div>
              {this.specificCategoriesRenderer()}
              {this.termListRenderer()}
              {this.peopleTabRenderer()}
              {this.atmosphereSliderRenderer()}

              <div
                className={classes.outerMargin}
                {...(!people.length && { style: { marginTop: 0 } })}
              >
                <Typography className={classes.subtitle}>
                  {T.translate('plan.contentSubtitle')}
                </Typography>
                {this.skillCategoriesRenderer()}
                {this.contentSummaryRenderer()}
              </div>
            </Grid>
            <Grid item xs={12} sm={5} lg={4} className={classes.rightGrid}>
              {this.favoriteRenderer()}
              <div>{this.businessInfoRenderer()}</div>
              <div>{this.businessAttractionsRenderer()}</div>
            </Grid>
          </Grid>
        </div>
        {this.otherRenderer()}
      </>
    );
  };

  render() {
    return (
      <PlanContext.Provider
        value={{
          plan: this.state,
          loaded: this.state.loaded,
          readOnly: true,
        }}
      >
        {this.contentRenderer()}
      </PlanContext.Provider>
    );
  }
}

export default Show;
